import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { getAppPortalRoot } from "../utils/appPortal";
import { API_URL } from "../config/api";
import PackAssignDropdown from "./PackAssignDropdown";
import "./DeleteWordModal.css";
import "./RegisterFilterModal.css";
import "./CaptureSourcePickerModal.css";

const FILTER_MENU_Z = 2147483600;
const EMPTY_OPTIONS = {
  auteurs: [],
  livres: [],
  byLivre: {},
  byAuteur: {},
};

function sortFr(values = []) {
  return [...values].sort((a, b) => a.localeCompare(b, "fr", { sensitivity: "base" }));
}

function toDropdownOptions(entries, { emptyValue = "", emptyLabel } = {}) {
  const options = entries.map((entry) =>
    typeof entry === "string"
      ? { value: entry, label: entry }
      : { value: entry.value, label: entry.label }
  );
  if (emptyLabel != null) {
    return [{ value: emptyValue, label: emptyLabel }, ...options];
  }
  return options;
}

function FilterDropdown({
  value,
  onChange,
  options,
  disabled = false,
  ariaLabel,
  menuMaxHeight = 170,
}) {
  return (
    <PackAssignDropdown
      options={options}
      value={value}
      onChange={onChange}
      disabled={disabled}
      ariaLabel={ariaLabel}
      matchTriggerWidth
      openBelow
      menuMaxHeight={menuMaxHeight}
      menuZIndex={FILTER_MENU_Z}
      menuClassName="register-filter-modal__dropdown-menu"
    />
  );
}

function FilterField({ label, children }) {
  return (
    <div className="register-filter-modal__field register-filter-modal__field--full">
      <span className="register-filter-modal__label">{label}</span>
      {children}
    </div>
  );
}

function reconcileLinkedFilters(draft, options) {
  const next = { ...draft };
  const byLivre = options.byLivre || {};
  const byAuteur = options.byAuteur || {};

  if (next.livre && byLivre[next.livre]) {
    next.auteur = byLivre[next.livre].auteur || next.auteur || "";
    return next;
  }

  if (next.auteur && byAuteur[next.auteur]) {
    const authorLivres = byAuteur[next.auteur].livres || [];
    if (next.livre && !authorLivres.includes(next.livre)) {
      next.livre = "";
    }
  }

  return next;
}

export default function CaptureSourcePickerModal({
  open,
  currentLivre = "",
  currentAuteur = "",
  onClose,
  onSelect,
}) {
  const [draft, setDraft] = useState({ livre: "", auteur: "" });
  const [options, setOptions] = useState(EMPTY_OPTIONS);
  const [loadingOptions, setLoadingOptions] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDraft({
      livre: String(currentLivre || "").trim(),
      auteur: String(currentAuteur || "").trim(),
    });
  }, [open, currentLivre, currentAuteur]);

  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    let cancelled = false;
    setLoadingOptions(true);

    fetch(`${API_URL}/mots-liste-detail/filter-options`)
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.message || "Impossible de charger les options.");
        if (!cancelled) setOptions({ ...EMPTY_OPTIONS, ...data });
      })
      .catch(() => {
        if (!cancelled) setOptions(EMPTY_OPTIONS);
      })
      .finally(() => {
        if (!cancelled) setLoadingOptions(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open || loadingOptions) return;
    setDraft((prev) => reconcileLinkedFilters(prev, options));
  }, [open, loadingOptions, options]);

  const livreSelected = Boolean(draft.livre);

  const availableLivres = useMemo(() => {
    if (livreSelected) {
      return options.livres || [];
    }
    if (draft.auteur && options.byAuteur?.[draft.auteur]?.livres) {
      return options.byAuteur[draft.auteur].livres;
    }
    return options.livres || [];
  }, [draft.auteur, draft.livre, livreSelected, options]);

  const lockedAuteur = livreSelected
    ? options.byLivre?.[draft.livre]?.auteur || draft.auteur
    : draft.auteur;

  const updateDraft = (patch) => {
    setDraft((prev) => {
      const next = { ...prev, ...patch };
      if ("livre" in patch) {
        if (patch.livre) {
          const meta = options.byLivre?.[patch.livre];
          if (meta?.auteur) next.auteur = meta.auteur;
        } else {
          next.auteur = "";
        }
      }
      if ("auteur" in patch && patch.auteur && !next.livre) {
        const authorLivres = options.byAuteur?.[patch.auteur]?.livres || [];
        if (next.livre && !authorLivres.includes(next.livre)) {
          next.livre = "";
        }
      }
      return next;
    });
  };

  const canValidate = Boolean(draft.livre || lockedAuteur || draft.auteur);

  const handleValidate = () => {
    if (!canValidate) return;
    onSelect?.({
      livre: draft.livre || "",
      auteur: lockedAuteur || draft.auteur || "",
    });
    onClose?.();
  };

  if (!open) return null;

  return createPortal(
    <div
      className="delete-word-modal-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose?.();
      }}
    >
      <div
        className="delete-word-modal register-filter-modal capture-source-picker"
        role="dialog"
        aria-modal="true"
        aria-labelledby="capture-source-picker-title"
      >
        <button
          type="button"
          className="delete-word-modal__close"
          onClick={onClose}
          aria-label="Fermer"
        >
          <X size={18} strokeWidth={2.5} />
        </button>

        <h3 id="capture-source-picker-title" className="register-filter-modal__title">
          Livre et auteur
        </h3>
        <p className="delete-word-modal__text register-filter-modal__intro">
          Choisissez un livre ou un auteur déjà présent dans le registre.
        </p>

        <div className="register-filter-modal__body">
          <div className="register-filter-modal__sections capture-source-picker__fields">
            <FilterField label="Livre">
              <FilterDropdown
                value={draft.livre}
                disabled={loadingOptions}
                ariaLabel="Choisir un livre"
                options={toDropdownOptions(availableLivres, {
                  emptyLabel: "— Tous les livres —",
                })}
                onChange={(next) => updateDraft({ livre: next })}
              />
            </FilterField>

            <FilterField label="Auteur">
              <FilterDropdown
                value={lockedAuteur || ""}
                disabled={loadingOptions || livreSelected}
                ariaLabel="Choisir un auteur"
                options={toDropdownOptions(
                  livreSelected && lockedAuteur ? [lockedAuteur] : sortFr(options.auteurs),
                  { emptyLabel: "— Tous les auteurs —" }
                )}
                onChange={(next) => updateDraft({ auteur: next })}
              />
            </FilterField>
          </div>
        </div>

        <div className="delete-word-modal__actions register-filter-modal__actions">
          <button
            type="button"
            className="delete-word-modal__btn delete-word-modal__btn--cancel"
            onClick={onClose}
          >
            Annuler
          </button>
          <button
            type="button"
            className="delete-word-modal__btn delete-word-modal__btn--confirm"
            onClick={handleValidate}
            disabled={!canValidate}
          >
            Valider
          </button>
        </div>
      </div>
    </div>,
    getAppPortalRoot()
  );
}
